<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'

import { fetchBetaPendingBills } from '@/services/beta-dashboard'
import { useApplicationStore } from '@/stores/application'
import { useSiteStore } from '@/stores/site'
import type { BetaSubscriptionBill } from '@/types/beta-dashboard'
import { buildCheckoutUrl } from '@/utils/checkout-url'

const emit = defineEmits<{
    subscriptions: []
}>()

const applicationStore = useApplicationStore()
const siteStore = useSiteStore()
const pendingBills = ref<BetaSubscriptionBill[]>([])

const siteIsInactive = computed(() => (
    siteStore.status === false
    || siteStore.status === 0
    || siteStore.status === '0'
))

const pendingBillCount = computed(() => pendingBills.value.length)
const shouldShow = computed(() => siteIsInactive.value || pendingBillCount.value > 0)
const canBrowseSubscriptions = computed(() => applicationStore.className !== 'epics')

const checkoutUrl = computed(() => {
    const pendingBillUrl = pendingBills.value.find((bill) => bill.link)?.link
    if (pendingBillUrl) return pendingBillUrl

    return buildCheckoutUrl(applicationStore.wizardUrl, siteStore.contract?.token)
})

const title = computed(() => {
    if (siteIsInactive.value) return 'Seu contrato precisa ser regularizado'
    return pendingBillCount.value === 1
        ? 'Você tem uma fatura em aberto'
        : `Você tem ${pendingBillCount.value} faturas em aberto`
})

const description = computed(() => {
    if (siteIsInactive.value && pendingBillCount.value > 0) {
        return 'Seu site está fora do ar. Regularize as faturas vencidas para liberar o acesso.'
    }

    if (siteIsInactive.value) {
        return 'O contrato do site está expirado ou bloqueado. Regularize a situação para voltar a publicá-lo.'
    }

    return 'Existem cobranças vencidas vinculadas ao seu site. Consulte os detalhes e faça o pagamento.'
})

const actionLabel = computed(() => (
    canBrowseSubscriptions.value && pendingBillCount.value > 1
        ? 'Ver faturas'
        : 'Regularizar agora'
))

const loadPendingBills = async () => {
    const siteId = Number(siteStore.id)
    if (!Number.isFinite(siteId) || siteId <= 0) return

    try {
        pendingBills.value = await fetchBetaPendingBills(siteId)
    } catch {
        pendingBills.value = []
    }
}

onBeforeMount(loadPendingBills)
</script>

<template>
    <article v-if="shouldShow" class="beta-financial-alert" role="alert">
        <div class="beta-financial-alert__icon" aria-hidden="true">!</div>

        <div class="beta-financial-alert__content">
            <span>Financeiro</span>
            <h2>{{ title }}</h2>
            <p>{{ description }}</p>
        </div>

        <button
            v-if="pendingBillCount && canBrowseSubscriptions"
            type="button"
            class="beta-financial-alert__action"
            @click="emit('subscriptions')"
        >
            {{ actionLabel }}
            <span aria-hidden="true">→</span>
        </button>

        <a
            v-else-if="checkoutUrl"
            class="beta-financial-alert__action"
            :href="checkoutUrl"
        >
            {{ actionLabel }}
            <span aria-hidden="true">→</span>
        </a>
    </article>
</template>

<style scoped>
.beta-financial-alert {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    border: 1px solid #f3c8c8;
    border-radius: 20px;
    background: linear-gradient(135deg, #fff8f8 0%, #fff2f2 100%);
    padding: 18px 20px;
}

.beta-financial-alert__icon {
    display: grid;
    width: 42px;
    height: 42px;
    place-items: center;
    border-radius: 14px;
    background: #b94b4b;
    color: #fff;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 20px;
    font-weight: 700;
}

.beta-financial-alert__content {
    min-width: 0;
}

.beta-financial-alert__content > span {
    color: #aa4c4c;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.beta-financial-alert__content h2 {
    margin-top: 3px;
    color: #6f3030;
    font-family: 'Outfit', ui-sans-serif, system-ui, sans-serif;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.2;
}

.beta-financial-alert__content p {
    margin-top: 4px;
    color: #966060;
    font-size: 11px;
    line-height: 1.5;
}

.beta-financial-alert__action {
    display: inline-flex;
    min-height: 40px;
    flex: none;
    align-items: center;
    justify-content: center;
    gap: 9px;
    border-radius: var(--beta-button-radius, 12px);
    background: #a94242;
    padding: 0 15px;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;
}

@media (max-width: 640px) {
    .beta-financial-alert {
        grid-template-columns: auto minmax(0, 1fr);
        align-items: start;
        padding: 16px;
    }

    .beta-financial-alert__icon {
        width: 38px;
        height: 38px;
        border-radius: 12px;
    }

    .beta-financial-alert__action {
        grid-column: 1 / -1;
        width: 100%;
    }
}
</style>
